import type { User } from "firebase/auth";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  evaluateMfaCompliance,
  isNonCompliantMfaVerdict,
  type MfaComplianceVerdict,
  withPreservedMfaReturnTo,
} from "./mfaCompliance";
import type { MfaEnrollmentFactor } from "./mfaEnrollment";
import { subscribeMfaStepUpRequired } from "./mfaStepUpSignal";

export type UseMfaComplianceOptions = {
  user: User | null;
  mfaRequired: boolean;
  organizationId?: number;
  getMfaEnrollmentFactors: () => Promise<MfaEnrollmentFactor[]>;
  getReturnTo?: () => string;
  enabled?: boolean;
  /**
   * React to a `mfa_step_up_required` refusal seen by the app's network layer. Opt-in: the signal
   * is module-global and several components use this hook, but only the app's entry gate should
   * turn a mid-session refusal into a redirect. The screen that *resolves* the requirement must
   * never subscribe.
   */
  reactToStepUpSignals?: boolean;
};

export type UseMfaComplianceResult = {
  verdict: MfaComplianceVerdict | null;
  checking: boolean;
  retry: () => void;
};

/**
 * `initial` is the mount decision. `recheck` is the focus/visibility re-take, which only ever
 * confirms or clears an existing non-compliant verdict. `step-up` is a refusal the server has
 * already stated, so it overrides all three of the guards the other modes rely on.
 */
type EvaluationMode = "initial" | "recheck" | "step-up";

function defaultGetReturnTo(): string {
  return `${window.location.pathname}${window.location.search}`;
}

function applyEvaluationResult(
  previous: MfaComplianceVerdict | null,
  next: MfaComplianceVerdict,
  preservedReturnTo: string | null,
  isReCheck: boolean,
): { verdict: MfaComplianceVerdict; preservedReturnTo: string | null } {
  // Focus re-check only: a network miss must not replace the enrollment/re-login screen
  // with "unavailable". Initial and step-up still accept that status.
  if (isReCheck && next.status === "unavailable" && previous !== null) {
    return { verdict: previous, preservedReturnTo };
  }

  // Re-checks often run from /security; stamp the first failure's URL so we still return
  // the user to the page they were on, not the resolve route.
  const merged = withPreservedMfaReturnTo(next, preservedReturnTo);
  let nextPreserved = preservedReturnTo;
  if (isNonCompliantMfaVerdict(merged)) {
    // First failure captures; later re-checks must not overwrite it.
    nextPreserved = nextPreserved ?? merged.returnTo;
  } else if (merged.status === "compliant") {
    nextPreserved = null;
  }

  return { verdict: merged, preservedReturnTo: nextPreserved };
}

type CompletedScope = {
  /** The exact scope the verdict was produced for. */
  key: string;
  /** User and `enabled`: what the verdict depends on apart from the organization. */
  session: string;
  /** Whether the verdict was produced with MFA actually checked. */
  mfaChecked: boolean;
};

/**
 * Whether a `compliant` verdict still holds after the scope changed, so it need not be re-derived.
 *
 * Compliance is a property of the session (an enrolled factor, a second-factor sign-in), not of the
 * organization. Moving between organizations — or between organization and global pages — changes
 * nothing it was based on, unless MFA was not checked before and is now required. Re-deriving it
 * anyway hands the gate a `null` verdict for a render, and the gate unmounts the page to show its
 * loader. Non-compliant verdicts are not carried: `impersonation_blocked` is organization-specific.
 */
function carriesCompliantVerdict(
  completed: CompletedScope | null,
  verdict: MfaComplianceVerdict | null,
  session: string,
  mfaRequired: boolean,
): completed is CompletedScope {
  return (
    verdict?.status === "compliant" &&
    completed !== null &&
    completed.session === session &&
    (completed.mfaChecked || !mfaRequired)
  );
}

/**
 * Verdict for whether the signed-in user currently satisfies MFA policy, so route gates can
 * redirect to enrollment or a fresh login instead of rendering protected UI.
 *
 * Policy can already apply at mount, start applying mid-session (`mfa_step_up_required`), or
 * clear after the user finishes on the resolve screen and comes back. This hook is the one
 * place that re-evaluates those cases without losing the original return URL.
 */
export function useMfaCompliance({
  user,
  mfaRequired,
  organizationId,
  getMfaEnrollmentFactors,
  getReturnTo = defaultGetReturnTo,
  enabled = true,
  reactToStepUpSignals = false,
}: UseMfaComplianceOptions): UseMfaComplianceResult {
  const [verdict, setVerdict] = useState<MfaComplianceVerdict | null>(null);
  const [checking, setChecking] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);
  const sessionScope = `${user?.uid ?? ""}/${enabled}`;
  const evaluationScope = `${sessionScope}/${organizationId ?? ""}/${mfaRequired}`;
  const completedScopeRef = useRef<CompletedScope | null>(null);
  const preservedReturnToRef = useRef<string | null>(null);
  // A focus re-check and a Retry can be in flight together, and a window switch can fire both
  // `focus` and `visibilitychange`. Without this, the slower run writes last regardless of which
  // started last — an older `needs_enrollment` could overwrite a fresh `compliant` and bounce a
  // compliant user back to the resolve route. Only the newest run may write.
  const latestRunIdRef = useRef(0);
  // A page with several parallel queries takes several 403s and fires several signals. One
  // re-evaluation answers all of them.
  const stepUpInFlightRef = useRef(false);
  const verdictRef = useRef<MfaComplianceVerdict | null>(null);
  const getMfaEnrollmentFactorsRef = useRef(getMfaEnrollmentFactors);
  const getReturnToRef = useRef(getReturnTo);

  useEffect(() => {
    verdictRef.current = verdict;
  }, [verdict]);

  useEffect(() => {
    getMfaEnrollmentFactorsRef.current = getMfaEnrollmentFactors;
    getReturnToRef.current = getReturnTo;
  }, [getMfaEnrollmentFactors, getReturnTo]);

  const runEvaluation = useCallback(
    async (mode: EvaluationMode) => {
      const isStepUp = mode === "step-up";
      const isReCheck = mode === "recheck";

      if (isReCheck) {
        const current = verdictRef.current;
        if (!current || !isNonCompliantMfaVerdict(current)) {
          return;
        }
      }

      if (!enabled) {
        setVerdict(null);
        setChecking(false);
        return;
      }

      if (!user) {
        setVerdict(null);
        setChecking(false);
        return;
      }

      const completed = completedScopeRef.current;
      if (mode === "initial" && carriesCompliantVerdict(completed, verdictRef.current, sessionScope, mfaRequired)) {
        completedScopeRef.current = { ...completed, key: evaluationScope };
        // A run this one superseded (e.g. a step-up check) can no longer clear `checking` itself.
        setChecking(false);
        return;
      }

      // `mfaRequired` is read from the permissions fetched when the session started, so in the
      // one case a step-up signal describes — the requirement started applying mid-session — it is
      // stale by definition. The server has already refused; treat the requirement as real.
      if (!mfaRequired && !isStepUp) {
        preservedReturnToRef.current = null;
        const compliant = { status: "compliant" } as const;
        completedScopeRef.current = { key: evaluationScope, session: sessionScope, mfaChecked: false };
        verdictRef.current = compliant;
        setVerdict(compliant);
        setChecking(false);
        return;
      }

      const runId = (latestRunIdRef.current += 1);
      if (isStepUp) {
        stepUpInFlightRef.current = true;
      }
      setChecking(true);
      try {
        let next = await evaluateMfaCompliance({
          user,
          mfaRequired: isStepUp ? true : mfaRequired,
          getMfaEnrollmentFactors: () => getMfaEnrollmentFactorsRef.current(),
          getReturnTo: () => getReturnToRef.current(),
        });
        // A focus re-check that cannot reach the server keeps the screen the user has. A step-up
        // signal is different: the refusal is a fact, and leaving `compliant` standing would leave
        // the user on a page whose requests all fail with nothing said.
        if (isStepUp && next.status === "unavailable") {
          next = {
            status: "needs_fresh_login",
            returnTo: getReturnToRef.current(),
          };
        }
        if (latestRunIdRef.current !== runId) {
          return;
        }
        const applied = applyEvaluationResult(verdictRef.current, next, preservedReturnToRef.current, isReCheck);
        completedScopeRef.current = { key: evaluationScope, session: sessionScope, mfaChecked: true };
        preservedReturnToRef.current = applied.preservedReturnTo;
        verdictRef.current = applied.verdict;
        setVerdict(applied.verdict);
      } finally {
        if (isStepUp) {
          stepUpInFlightRef.current = false;
        }
        if (latestRunIdRef.current === runId) {
          setChecking(false);
        }
      }
    },
    [enabled, mfaRequired, user, organizationId, evaluationScope, sessionScope],
  );

  useEffect(() => {
    latestRunIdRef.current += 1;
    stepUpInFlightRef.current = false;
    preservedReturnToRef.current = null;
    void runEvaluation("initial");
    return () => {
      latestRunIdRef.current += 1;
    };
  }, [runEvaluation, retryNonce]);

  useEffect(() => {
    if (!enabled || !user || !mfaRequired) {
      return;
    }

    const maybeReCheck = () => {
      const current = verdictRef.current;
      if (!current || !isNonCompliantMfaVerdict(current)) {
        return;
      }
      void runEvaluation("recheck");
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        maybeReCheck();
      }
    };

    window.addEventListener("focus", maybeReCheck);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.removeEventListener("focus", maybeReCheck);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [enabled, mfaRequired, runEvaluation, user]);

  useEffect(() => {
    if (!reactToStepUpSignals || !enabled || !user) {
      return;
    }
    return subscribeMfaStepUpRequired((signal) => {
      if (signal?.userId && signal.userId !== user.uid) return;
      if (signal?.detail?.organization_id !== undefined && signal.detail.organization_id !== organizationId) return;
      if (signal?.detail?.reason === "impersonation_not_supported") {
        // This cancels any evaluation in flight, so it must mark the scope complete itself, or the
        // hook keeps returning `null` for a scope no evaluation will now finish.
        latestRunIdRef.current += 1;
        completedScopeRef.current = { key: evaluationScope, session: sessionScope, mfaChecked: true };
        verdictRef.current = { status: "impersonation_blocked" };
        setVerdict(verdictRef.current);
        setChecking(false);
        return;
      }
      if (stepUpInFlightRef.current) {
        return;
      }
      void runEvaluation("step-up");
    });
  }, [enabled, reactToStepUpSignals, runEvaluation, user, organizationId, evaluationScope, sessionScope]);

  const retry = useCallback(() => {
    setRetryNonce((value) => value + 1);
  }, []);

  const completed = completedScopeRef.current;
  const current =
    completed?.key === evaluationScope || carriesCompliantVerdict(completed, verdict, sessionScope, mfaRequired);
  return { verdict: current ? verdict : null, checking, retry };
}
